<script setup lang="ts">
import { ref, watch, Ref } from "vue";
import { debounce } from "vue-debounce";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import useDialog from "@/ui/composables/useDialog";
import AddEditGroupingDialog from "@/modules/campaign_planner/components/screens/AddEditGroupingDialog.vue";
import ProductPoolDO = Modules.CampaignPlanner.Data.ProductPoolDO;
import ScreenDO = Modules.CampaignPlanner.Data.ScreenDO;

type BoolFilter = null | boolean;

interface TableOptions {
	page: number;
	itemsPerPage: number;
	sortBy: { key: string; order: "asc" | "desc" }[];
}

const headers = [
	{ title: "Identifier", key: "identifier", align: "start", sortable: true },
	{ title: "Screens", key: "screens_count", align: "center", sortable: false },
	{
		title: "Single location",
		key: "is_single_location_for_spread_algorithm",
		align: "center",
		sortable: true,
	},
	{
		title: "PQA amounts",
		key: "pqa_allowed_amounts_screens",
		align: "start",
		sortable: false,
	},
	{
		title: "Actions",
		key: "actions",
		align: "end",
		sortable: false,
		width: 100,
	},
] as const;

const pools: Ref<ProductPoolDO[]> = ref([]);
const loading: Ref<boolean | string> = ref(false);
const total = ref(0);
const search = ref("");
const filterText = ref("");
const tableOptions = ref<TableOptions>({
	page: 1,
	itemsPerPage: 25,
	sortBy: [],
});

const filters = ref<{
	is_single_location_for_spread_algorithm: BoolFilter;
}>({
	is_single_location_for_spread_algorithm: null,
});

const isDialogVisible = ref(false);
const poolToEdit = ref<ProductPoolDO | null>(null);

watch(filterText, () => (search.value = String(Date.now())));
watch(filters, () => (search.value = String(Date.now())), { deep: true });

const buildFilterParams = (): Record<string, string> => {
	const params: Record<string, string> = {};

	if (filters.value.is_single_location_for_spread_algorithm !== null) {
		params["filter[is_single_location_for_spread_algorithm]"] = filters.value
			.is_single_location_for_spread_algorithm
			? "true"
			: "false";
	}

	if (filterText.value) params["filter[identifier]"] = filterText.value;

	return params;
};

const loadPools = debounce(async (options: TableOptions) => {
	tableOptions.value = options;
	loading.value = "secondary";

	const { page, itemsPerPage, sortBy } = options;
	const sort = sortBy[0]
		? (sortBy[0].order === "desc" ? "-" : "") + sortBy[0].key
		: undefined;

	const response = await useApiClient("campaignPlanner").getProductPools({
		page,
		per_page: itemsPerPage === -1 ? undefined : itemsPerPage,
		sort,
		...buildFilterParams(),
	});

	if (response.isOk()) {
		pools.value = response.value.data;
		total.value = response.value.total;
	}

	loading.value = false;
}, 300);

const openAdd = () => {
	poolToEdit.value = null;
	isDialogVisible.value = true;
};

const openEdit = (pool: ProductPoolDO) => {
	poolToEdit.value = pool;
	isDialogVisible.value = true;
};

const deletePool = async (pool: ProductPoolDO) => {
	const confirmed = await useDialog().confirm({
		title: "Delete group",
		message: `Are you sure you want to delete "${pool.identifier}"?`,
		isDelete: true,
	});

	if (!confirmed) return;

	const response = await toastApiResult(
		useApiClient("campaignPlanner").deleteProductPool(pool.uuid)
	);

	if (response.isOk()) {
		loadPools(tableOptions.value);
	}
};

const screenCount = (pool: ProductPoolDO) =>
	(pool.screens as ScreenDO[] | undefined)?.length ?? 0;
</script>

<template>
	<add-edit-grouping-dialog
		v-if="isDialogVisible"
		v-model="isDialogVisible"
		:pool-to-edit="poolToEdit"
		@update-list="loadPools(tableOptions)"
	/>

	<v-card elevation="1" class="mt-5">
		<div class="card-header">
			<v-text-field
				v-model="filterText"
				prepend-inner-icon="fa: fa-regular fa-magnifying-glass"
				placeholder="Search groups…"
				clearable
				hide-details
				variant="outlined"
				density="compact"
			/>

			<div class="filter-strip">
				<div class="filter-group">
					<span class="text-caption text-medium-emphasis">Single location</span>
					<v-btn-toggle
						v-model="filters.is_single_location_for_spread_algorithm"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
						rounded="lg"
					>
						<v-btn :value="null" size="small">All</v-btn>
						<v-btn :value="true" size="small">Yes</v-btn>
						<v-btn :value="false" size="small">No</v-btn>
					</v-btn-toggle>
				</div>

				<div class="filter-group align-self-end ml-auto">
					<v-btn
						color="primary"
						size="small"
						prepend-icon="fa: fa-regular fa-plus"
						@click="openAdd"
					>
						Add group
					</v-btn>
				</div>
			</div>
		</div>

		<v-divider />

		<v-data-table-server
			:headers="headers"
			:items="pools"
			:items-per-page="tableOptions.itemsPerPage"
			:page="tableOptions.page"
			:items-length="total"
			:loading="loading"
			:search="search"
			:multi-sort="false"
			class="elevation-0"
			@update:options="loadPools"
		>
			<template v-slot:item.screens_count="{ item }">
				<v-chip
					size="small"
					:color="screenCount(item) > 0 ? 'primary' : undefined"
					variant="tonal"
				>
					{{ screenCount(item) }}
				</v-chip>
			</template>

			<template v-slot:item.is_single_location_for_spread_algorithm="{ item }">
				<div v-if="item.is_single_location_for_spread_algorithm">
					<v-icon color="success" size="small">
						fa: fa-solid fa-circle-check
					</v-icon>
				</div>
				<div v-else>
					<v-icon color="medium-emphasis" size="small">
						fa: fa-regular fa-circle-xmark
					</v-icon>
				</div>
			</template>

			<template v-slot:item.pqa_allowed_amounts_screens="{ item }">
				<span class="text-body-2">
					{{ item.pqa_allowed_amounts_screens?.join(", ") ?? "-" }}
				</span>
			</template>

			<template v-slot:item.actions="{ item }">
				<v-btn
					size="x-small"
					icon="fa: fa-regular fa-pen"
					color="primary"
					variant="text"
					@click="openEdit(item)"
				/>
				<v-btn
					size="x-small"
					icon="fa: fa-regular fa-trash"
					color="error"
					variant="text"
					@click="deletePool(item)"
				/>
			</template>
		</v-data-table-server>
	</v-card>
</template>

<style scoped lang="scss">
.card-header {
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.filter-strip {
	display: flex;
	flex-wrap: wrap;
	gap: 8px 20px;
	align-items: flex-end;
}

.filter-group {
	display: flex;
	flex-direction: column;
	gap: 4px;
}
</style>
